import type {CSSProperties} from 'react';
import {radius, weight} from '../scale.js';
import {wedgeExactRole, wedgeRole} from './wedgeRoles.js';

export type WedgeLeadsImportLook = 'import-modal-close' | 'import-icon' | 'import-tab' | 'import-tab-on' | 'import-file' | 'import-camera' | 'import-read';
export type WedgeLeadsImportAreaVariant = 'wedge-import-paste';

const IMPORT_LOOKS = new Set<string>(['import-modal-close', 'import-icon', 'import-tab', 'import-tab-on', 'import-file', 'import-camera', 'import-read']);
export const isWedgeLeadsImportLook = (look: string): look is WedgeLeadsImportLook => IMPORT_LOOKS.has(look);
export const isWedgeLeadsImportAreaVariant = (variant: unknown): variant is WedgeLeadsImportAreaVariant => variant === 'wedge-import-paste';

/* Released lead import sheet and Pipeline modal (prep-leads PipelineModal and PipelineImportSheet), off-scale. The close mark of the modal
   header is a 44px square with a 10px radius on the surface fill and a hairline card border, its glyph centered. The back and close marks
   of the sheet header are the same box with a 44px minimum width. The tabs (From a file, Paste) share the strip: each grows to fill, is 44px
   tall at least, has a 9px radius, no border, 9px by 6px of padding and 13.5px bold type; the chosen tab takes the accent fill and the
   on-accent ink, the other is clear in the secondary ink. Choose a file is the primary button: a 12px radius box on the accent fill with
   the on-accent ink, 15px by 22px of padding, 16px type at weight 800, a 8px gap and a 26px accent glow at 30%; the caller sets the minimum
   width, which depends on the layout. Take a photo is the ghost button: full width, 44px tall at least, 13px by 16px of padding, 12px
   radius, the surface fill and card border, 15px type at weight 600 in the label ink. Read my list is the primary box at its base size
   (14px by 18px, 15px type), full width with a 12px top margin; held with `disabled` it takes the default cursor and half opacity.
   The raw buttons set no font family: the class of the sheet gives them the body face. Each color reads the exact legacy role, so the
   shipped ink rules keep matching. No look draws a transparent target (wedgeLeadsImportDrawsHit): every box is 44px or more each way, and
   a region could only repeat the box. */
export const wedgeLeadsImportDrawsHit = (look: string): boolean => !look.startsWith('import-');

const row = {display: 'inline-flex', alignItems: 'center', justifyContent: 'center'} as const;
const glow = (pct: number) => `color-mix(in srgb, ${wedgeExactRole('accent')} ${pct}%, transparent)`;

export function wedgeLeadsImportStyle(look: WedgeLeadsImportLook, disabled = false): CSSProperties {
  const r = wedgeExactRole;
  const box = {borderRadius: radius.md, background: r('surface'), border: `1px solid ${r('cardBorder')}`, cursor: 'pointer', ...row} as const;
  const primary = {
    padding: '14px 18px', borderRadius: radius.lg, background: r('accent'), border: 0, color: wedgeRole('onAccent'), fontWeight: weight.heavy,
    fontSize: 15, cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.5 : 1, ...row, gap: 8,
  } as const;
  switch (look) {
    case 'import-modal-close': return {width: 44, height: 44, ...box};
    case 'import-icon': return {width: 44, height: 44, minWidth: 44, ...box};
    case 'import-tab':
    case 'import-tab-on': {
      const on = look === 'import-tab-on';
      return {
        flex: 1, minHeight: 44, padding: '9px 6px', borderRadius: 9, border: 0, cursor: 'pointer', background: on ? r('accent') : 'transparent',
        color: on ? wedgeRole('onAccent') : r('labelSec'), fontWeight: weight.bold, fontSize: 13.5,
      };
    }
    case 'import-file': return {...primary, padding: '15px 22px', fontSize: 16, boxShadow: `0 10px 26px ${glow(30)}`};
    case 'import-camera': return {
      minHeight: 44, padding: '13px 16px', borderRadius: radius.lg, background: r('surface'), border: `1px solid ${r('cardBorder')}`,
      color: r('label'), fontWeight: weight.semibold, fontSize: 15, cursor: 'pointer', ...row, gap: 8, width: '100%',
    };
    case 'import-read': return {...primary, width: '100%', marginTop: 12};
  }
}

export function wedgeLeadsImportAreaStyle(_variant: WedgeLeadsImportAreaVariant): CSSProperties {
  const r = wedgeExactRole;
  return {
    width: '100%', boxSizing: 'border-box', minHeight: 170, background: r('surface'), border: `1px solid ${r('cardBorder')}`, borderRadius: radius.lg,
    padding: 12, color: r('label'), fontFamily: r('mono'), fontSize: 12.5, outline: 'none', resize: 'vertical',
  };
}
